/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { containerEventsClient, ContainerStatusEvent } from '../services/containerEvents';

/**
 * Subscribes to realtime container status events for a project and patches
 * the react-query caches in place so status flips instantly without refetch.
 * Returns the socket connection state so pages can relax their polling
 * fallback while connected.
 */
export const useContainerEvents = (projectId: string | null | undefined): { connected: boolean } => {
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(containerEventsClient.connected);

  useEffect(() => {
    if (!projectId) return;
    containerEventsClient.connect(projectId);

    const offState = containerEventsClient.onStateChange(setConnected);
    const offEvent = containerEventsClient.onEvent((ev: ContainerStatusEvent) => {
      // failure_reason is only sent on "failed" events, so patching it
      // unconditionally also clears the banner when the pod recovers.
      const fields = { status: ev.status, failure_reason: ev.failure_reason };
      // Detail cache: shape is either {container: {...}} or the container itself
      queryClient.setQueryData(['container', ev.container_id], (old: any) => {
        if (!old) return old;
        if (old.container) {
          return { ...old, container: { ...old.container, ...fields } };
        }
        return { ...old, ...fields };
      });
      // List cache: array, {data: [...]}, or {containers: [...]}
      queryClient.setQueryData(['containers', projectId], (old: any) => {
        if (!old) return old;
        const patch = (arr: any[]) =>
          arr.map((c) => (c.id === ev.container_id ? { ...c, ...fields } : c));
        if (Array.isArray(old)) return patch(old);
        if (Array.isArray(old.data)) return { ...old, data: patch(old.data) };
        if (Array.isArray(old.containers)) return { ...old, containers: patch(old.containers) };
        return old;
      });
    });

    return () => {
      offState();
      offEvent();
    };
  }, [projectId, queryClient]);

  return { connected };
};
